import { Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useTriggerAIOnboardingWorkflow } from '@/modules/aiOnboarding/hooks/useTriggerAIOnboardingWorkflow';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { useDocumentTypesValidation } from '@/modules/documents/MultiDocumentUploader/hooks/useDocumentTypesValidation';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { DocumentType, WorkflowTrackerKind } from '@/types/schema';

import {
  ClientOnboardingDeliverableModalBodyProps,
  ClientOnboardingDeliverableStep,
} from '../../types';
import { DocumentProcessingCompletionButton } from '../components/DocumentProcessingCompletionButton';
import { DocumentProcessingStatus } from '../components/DocumentProcessingStatus';
import { DocumentUploadWithFrame } from '../components/DocumentUploadWithFrame';

export interface GenericDocumentProcessingConfig {
  // Upload phase configuration
  allowSelectExistingDocuments: boolean;
  subheader: string;

  // Processing configuration
  workflowTrackerKind: WorkflowTrackerKind;
  useAIWorkflow: boolean; // true for AI onboarding, false for document upload

  // Processing phase messages
  processingHeader: (documentNoun: string) => string;
  processingSubheader: React.ReactNode;
  successHeader: string;
  successSubheader: string;
  errorMessage: string;

  // Completion configuration
  completionButtonLabel: string;
  onCompletionClick: (
    navigate: ReturnType<typeof useNavigateToRoute>['navigate'],
    householdId: string
  ) => void;

  // Additional content for upload phase (optional)
  additionalUploadContentAbove?: React.ReactNode;

  // Additional content for upload phase (optional)
  additionalUploadContentBelow?: React.ReactNode;
}

interface GenericDocumentProcessingModalBodyProps
  extends ClientOnboardingDeliverableModalBodyProps {
  config: GenericDocumentProcessingConfig;
}

function GenericDocumentProcessingModalBodyInner({
  onChildPhaseChange,
  onClose,
  config,
}: GenericDocumentProcessingModalBodyProps) {
  const { navigate } = useNavigateToRoute();
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<string[]>([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [phase, setPhase] = useState<ClientOnboardingDeliverableStep>(
    ClientOnboardingDeliverableStep.UPLOAD
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handlePhaseChange = useCallback(
    (phase: ClientOnboardingDeliverableStep) => {
      setPhase(phase);
      onChildPhaseChange?.(phase);
    },
    [onChildPhaseChange]
  );

  const { householdId } = useHouseholdDetailsContext();
  const { uploadedFiles, selectedExistingDocuments, uploadDocuments } =
    useMultiDocumentUploaderContext();
  const { triggerWorkflow, loading: loadingTriggerWorkflow } =
    useTriggerAIOnboardingWorkflow();

  const relevantFilesCount = [...uploadedFiles, ...selectedExistingDocuments]
    .length;
  const documentNoun = relevantFilesCount === 1 ? 'document' : 'documents';
  const allDocumentTypesSelected = useDocumentTypesValidation();

  const handleProcessDocuments = useCallback(async () => {
    if (!householdId) {
      throw new Error('Submitting form with no householdId');
    }

    setUploadingDocuments(true);

    try {
      if (config.useAIWorkflow) {
        await triggerWorkflow({
          householdId,
          onSuccess: () => {
            setUploadingDocuments(false);
          },
          onError: () => {
            handlePhaseChange(ClientOnboardingDeliverableStep.UPLOAD);
            setErrorMessage(config.errorMessage);
            setUploadingDocuments(false);
          },
          onDocumentsUploaded: (documentIds) => {
            setUploadedDocumentIds(documentIds);
            handlePhaseChange(ClientOnboardingDeliverableStep.PROCESSING);
            setUploadingDocuments(false);
          },
        });
      } else {
        // For document summary workflow
        const documentIds = await uploadDocuments({
          householdId,
          defaultDocumentType: DocumentType.Other,
        });
        setUploadedDocumentIds(documentIds);
        handlePhaseChange(ClientOnboardingDeliverableStep.PROCESSING);
        setUploadingDocuments(false);
      }
    } catch {
      setUploadingDocuments(false);
    }
  }, [
    handlePhaseChange,
    householdId,
    triggerWorkflow,
    uploadDocuments,
    config,
  ]);

  if (!householdId) {
    return null;
  }

  const adjective =
    uploadedFiles.length === 0 && selectedExistingDocuments.length > 0
      ? 'selected'
      : 'uploaded';

  const renderUploadPhase = () => {
    return (
      <Stack spacing={2}>
        {config.additionalUploadContentAbove}
        <DocumentUploadWithFrame
          allowSelectExistingDocuments={config.allowSelectExistingDocuments}
          householdId={householdId}
          hideHeader={relevantFilesCount === 0}
          header={`${relevantFilesCount} ${documentNoun} ${adjective}`}
          subheader={config.subheader}
          errorMessage={errorMessage}
          onClearError={() => setErrorMessage(null)}
        />
        {config.additionalUploadContentBelow}
        {relevantFilesCount > 0 && (
          <Button
            variant="primary"
            size="sm"
            fullWidth
            onClick={handleProcessDocuments}
            loading={uploadingDocuments || loadingTriggerWorkflow}
            disabled={!allDocumentTypesSelected}
          >
            Process {documentNoun}
          </Button>
        )}
      </Stack>
    );
  };

  const renderProcessingPhase = () => {
    const headerDetails =
      phase === ClientOnboardingDeliverableStep.PROCESSING
        ? {
            header: config.processingHeader(documentNoun),
            subheader: config.processingSubheader,
          }
        : {
            header: config.successHeader,
            subheader: config.successSubheader,
          };

    return (
      <Stack spacing={2}>
        <DocumentProcessingStatus
          uploadedDocumentIds={uploadedDocumentIds}
          workflowTrackerKind={config.workflowTrackerKind}
          householdId={householdId}
          header={headerDetails.header}
          subheader={headerDetails.subheader}
          onAllProcessed={() =>
            handlePhaseChange(ClientOnboardingDeliverableStep.REVIEW)
          }
        />
        <Stack spacing={2}>
          <DocumentProcessingCompletionButton
            completionButtonLabel={config.completionButtonLabel}
            onCompletionClick={() =>
              config.onCompletionClick(navigate, householdId)
            }
          />
          {phase === ClientOnboardingDeliverableStep.PROCESSING && onClose && (
            <Typography variant="body-xs" textAlign="center">
              Progress saved in background — you can safely{' '}
              <Button
                variant="transparent"
                size="sm"
                onClick={onClose}
                sx={{
                  textDecoration: 'underline',
                  p: 0,
                  minWidth: 'auto',
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                close this window
              </Button>
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack
      padding={4}
      sx={{
        maxWidth: 640,
        margin: '0 auto',
        minHeight: '100%',
        justifyContent: 'center',
      }}
    >
      {phase === ClientOnboardingDeliverableStep.UPLOAD && renderUploadPhase()}
      {(phase === ClientOnboardingDeliverableStep.PROCESSING ||
        phase === ClientOnboardingDeliverableStep.REVIEW) &&
        renderProcessingPhase()}
    </Stack>
  );
}

export function GenericDocumentProcessingModalBody(
  props: GenericDocumentProcessingModalBodyProps
) {
  return <GenericDocumentProcessingModalBodyInner {...props} />;
}
